import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ShieldCheck, BadgeCheck, Loader2, Copy, Check, ArrowRight, Lock, Atom } from "lucide-react";
import { BloqLogo } from "@/components/brand/BloqLogo";
import { ShareToXButton } from "@/components/common/ShareToX";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { TopConnectorCrown } from "@/components/common/TopConnectorCrown";

const SHARE_BASE = process.env.REACT_APP_SHARE_BASE || "https://bloqtalk.app";

const BAND_ACCENT = {
  Trusted: "text-bloq-mint",
  Established: "text-bloq-blue",
  Verified: "text-bloq-mint",
  New: "text-bloq-muted",
  Watch: "text-amber-400",
};

const COMPONENT_LABELS = {
  identity: "Identity",
  behavior: "Behavior",
  contribution: "Contribution",
  standing: "Standing",
};

function ScoreRing({ score }) {
  const pct = Math.max(0, Math.min(100, Number(score) || 0));
  const r = 52;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative flex h-32 w-32 shrink-0 items-center justify-center">
      <svg viewBox="0 0 120 120" className="h-32 w-32 -rotate-90">
        <bloq cx="60" cy="60" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="10" />
        <bloq
          cx="60" cy="60" r={r} fill="none" stroke="url(#repGrad)" strokeWidth="10" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)}
        />
        <defs>
          <linearGradient id="repGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#01FABD" />
            <stop offset="100%" stopColor="#3591F4" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute text-center">
        <div className="font-heading text-4xl font-bold leading-none">{pct}</div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-bloq-muted">BLOQ Score</div>
      </div>
    </div>
  );
}

function ComponentBar({ label, value }) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div data-testid={`rep-component-${label}`}>
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="text-bloq-muted">{COMPONENT_LABELS[label] || label}</span>
        <span className="font-semibold text-white/90">{pct}</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-white/8">
        <div className="h-full rounded-full bg-gradient-to-r from-bloq-mint to-bloq-blue" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function PageFrame({ children }) {
  return (
    <div className="min-h-screen bg-bloq-bg text-white">
      <div className="mx-auto flex max-w-lg flex-col px-5 py-8 sm:py-14">
        <Link to="/" className="mb-8 flex items-center gap-2" data-testid="rep-home-link">
          <BloqLogo size={26} />
        </Link>
        {children}
      </div>
    </div>
  );
}

export default function PublicReputation() {
  const { handle } = useParams();
  const [copied, setCopied] = useState(false);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["public-reputation", handle],
    queryFn: () => api.publicReputation(handle),
    enabled: !!handle,
    retry: false,
  });

  // Unfurl-friendly share link: /api/u/<handle>?s&b&v renders a rich OG "trust passport" card
  // (QRNG ACTIVE badge) for X/Telegram/Discord, then redirects humans to the clean /u/<handle> page.
  const shareUrl = data
    ? `${SHARE_BASE}/api/u/${encodeURIComponent(handle)}?s=${data.score || 0}&b=${encodeURIComponent(data.band || "")}&v=${data.verified ? 1 : 0}`
    : (typeof window !== "undefined" ? `${window.location.origin}/u/${handle}` : `/u/${handle}`);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard unavailable */ }
  };

  if (isLoading) {
    return (
      <PageFrame>
        <div className="flex justify-center py-20 text-bloq-muted">
          <Loader2 className="h-7 w-7 animate-spin" />
        </div>
      </PageFrame>
    );
  }

  if (isError || !data) {
    return (
      <PageFrame>
        <div className="rounded-3xl border border-white/10 bg-bloq-s1 p-8 text-center" data-testid="rep-not-found">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/5 text-bloq-muted">
            <Lock className="h-6 w-6" />
          </div>
          <h1 className="text-lg font-semibold">Trust profile unavailable</h1>
          <p className="mt-1 text-sm text-bloq-muted">
            This profile is private, or the handle doesn't exist on BLOQ Talk.
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 rounded-full bloq-gradient px-5 py-2.5 text-sm font-bold text-bloq-bg transition-opacity hover:opacity-90"
            data-testid="rep-cta-home"
          >
            Explore BLOQ Talk <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </PageFrame>
    );
  }

  const band = data.band || "New";
  const accent = BAND_ACCENT[band] || "text-bloq-mint";
  const initial = (data.display_name || data.handle || "?").charAt(0).toUpperCase();
  const since = data.member_since ? new Date(data.member_since).toLocaleDateString(undefined, { month: "long", year: "numeric" }) : null;
  const components = data.components || {};

  return (
    <PageFrame>
      <div className="mb-3 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-bloq-mint">
        <ShieldCheck className="h-3 w-3" /> Verified trust profile
      </div>

      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-b from-bloq-s2 to-bloq-s1 p-7" data-testid="reputation-card">
        <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-bloq-mint/15 blur-3xl" />

        <div className="relative flex items-center gap-5">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-bloq-mint/80 to-bloq-blue/80 font-heading text-2xl font-bold text-bloq-bg">
            {initial}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <h1 className="truncate text-xl font-bold" data-testid="rep-display-name">{data.display_name || data.handle}</h1>
              {data.verified && <BadgeCheck className="h-5 w-5 shrink-0 text-bloq-mint" data-testid="rep-verified-badge" />}
              <TopConnectorCrown match={{ id: data.id, username: data.handle, handle: data.handle }} iconOnly />
            </div>
            <div className="truncate text-sm text-bloq-muted" data-testid="rep-handle">@{data.handle}</div>
            <div className={cn("mt-1.5 inline-flex items-center gap-1 rounded-full border border-white/12 px-2.5 py-0.5 text-xs font-semibold", accent)} data-testid="rep-band">
              <ShieldCheck className="h-3 w-3" /> {band}
            </div>
          </div>
        </div>

        <div className="relative mt-7 flex items-center gap-6">
          <ScoreRing score={data.score} />
          <div className="min-w-0 flex-1 space-y-2.5">
            {Object.keys(COMPONENT_LABELS)
              .filter((k) => k in components)
              .map((k) => (
                <ComponentBar key={k} label={k} value={components[k]} />
              ))}
          </div>
        </div>

        {since && (
          <div className="relative mt-6 border-t border-white/8 pt-4 text-xs text-bloq-muted" data-testid="rep-member-since">
            Member since {since}
          </div>
        )}

        <div className="relative mt-5 flex items-center gap-2.5 rounded-xl border border-bloq-purple/30 bg-bloq-purple/10 px-3 py-2.5" data-testid="rep-quantum-pill">
          <Atom className="h-4 w-4 shrink-0 text-bloq-purple" />
          <div className="text-xs leading-snug">
            <span className="font-semibold text-bloq-purple">QRNG Active</span>
            <span className="text-bloq-muted"> · secured on the only quantum-secured messenger — post-quantum Cipher Lock with QRNG-seeded keys</span>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2" data-testid="rep-share-row">
        <button
          onClick={copy}
          className="flex flex-1 items-center justify-center gap-2 rounded-2xl border border-white/12 bg-bloq-s1 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/5"
          data-testid="rep-copy-link-btn"
        >
          {copied ? <><Check className="h-4 w-4 text-bloq-mint" /> Link copied</> : <><Copy className="h-4 w-4" /> Copy share link</>}
        </button>
        <ShareToXButton
          text={`@${data.handle} has a BLOQ Score of ${data.score} 🛡️ — verified on the only quantum-secured Web3 messenger. Check their trust profile 👇`}
          url={shareUrl}
          className="border border-white/12 bg-white px-5 py-3 font-bold text-black hover:opacity-90"
          testid="rep-share-x-btn"
        />
      </div>

      {/* Viral loop CTA */}
      <div className="mt-6 rounded-3xl border border-bloq-mint/25 bg-bloq-mint/10 p-6 text-center" data-testid="rep-join-cta">
        <h2 className="text-base font-semibold">Get your own BLOQ trust profile</h2>
        <p className="mt-1 text-sm text-bloq-muted">
          Build a portable, verifiable reputation across Web3 — and message with people you can actually trust.
        </p>
        <Link
          to="/signup"
          className="mt-4 inline-flex items-center gap-2 rounded-full bloq-gradient px-6 py-2.5 text-sm font-bold text-bloq-bg transition-opacity hover:opacity-90"
          data-testid="rep-signup-cta"
        >
          Join BLOQ Talk <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </PageFrame>
  );
}
